import { PoiDataService, DetailedForecastData, getBaseElevationAt, isPointInUS, isPointInCanada, isModelAvailableAt } from '../../services/PoiDataService';
import { ClimateService } from '../../services/ClimateService';
import TierAccessService from '../../services/TierAccessService';
import { ClimateModal } from './ClimateModal';
import store from '../../state';
import maplibregl from 'maplibre-gl';
import { Chart, registerables } from 'chart.js';
import { getActivePersona } from '../../personas/registry';
import { SoundingChart } from '../SoundingChart';
import { getLayoutForPersona, renderLayoutHtml, initLayoutWidgets, getRequirementsForPersona } from './detailed_forecast/WidgetRegistry';
import { WidgetRenderContext } from './detailed_forecast/types';

Chart.register(...registerables);

export class PoiModal {
  private container: HTMLElement;
  private auroraMaps: maplibregl.Map[] = [];
  private charts: Chart[] = [];
  private soundingChart: SoundingChart | null = null;

  private activeTabId: string = 'blend';
  private selectedSource: string = 'auto';
  private currentLat: number = 0;
  private currentLon: number = 0;
  private currentElev: number = 0;
  private currentDateStr: string = '';
  private currentBaseElev: number = 0;
  private isMountain: boolean = false;
  private currentForecastData: DetailedForecastData | null = null;

  private viewDays: number | string = 1;
  private lightningMaps: maplibregl.Map[] = [];
  private unsubscribeStore: (() => void) | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'poi-modal-container';
    this.container.style.display = 'none';
    this.container.style.position = 'fixed';
    this.container.style.top = '0';
    this.container.style.left = '0';
    this.container.style.width = '100vw';
    this.container.style.height = '100vh';
    this.container.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
    this.container.style.backdropFilter = 'blur(10px)';
    (this.container.style as any).WebkitBackdropFilter = 'blur(10px)';
    this.container.style.zIndex = '9999';
    this.container.style.alignItems = 'center';
    this.container.style.justifyContent = 'center';
    
    // Clicking outside closes the modal
    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.close();
      }
    });

    document.body.appendChild(this.container);
  }

  private closeChartsAndMaps() {
    if (this.soundingChart) {
      this.soundingChart.destroy();
      this.soundingChart = null;
    }
    this.charts.forEach(c => c.destroy());
    this.charts = [];
    
    this.lightningMaps.forEach(m => m.remove());
    this.lightningMaps = [];
    
    this.auroraMaps.forEach(m => m.remove());
    this.auroraMaps = [];
  }

  public close() {
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    this.container.style.display = 'none';
    this.container.innerHTML = '';
    this.closeChartsAndMaps();
  }

  public getCurrentForecastData(): DetailedForecastData | null {
    return this.currentForecastData;
  }

  public async open(lat: number, lon: number, elev: number, _dateStr?: string, defaultTab?: string) {
    if (defaultTab === 'climate') {
      const state = store.getState();
      const title = (state.selectedPoi as any)?.name || (state.selectedPoi as any)?.title || undefined;
      ClimateModal.getInstance().open(lat, lon, elev, title);
      return;
    }

    this.container.style.display = 'flex';
    this.container.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: white; font-size: 1.5rem;">Loading Detailed Forecast...</div>`;

    this.currentLat = lat;
    this.currentLon = lon;
    this.currentElev = elev;
    // Always anchor the full detailed forecast modal to today's local date at the POI
    const todayDateStr = PoiDataService.getTodayLocalDateStr(lat, lon);
    this.currentDateStr = todayDateStr;
    this.isMountain = elev > 3000;
    this.viewDays = 1; // Default to 1-Day on initial open

    const state = store.getState();
    
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    let lastPersona = state.activePersona;
    let lastTier = TierAccessService.getUserAccessTier(state);
    let lastToken = state.sessionToken;
    this.unsubscribeStore = store.subscribe(async (newState) => {
      if (this.container.style.display === 'none') return;
      const nextTier = TierAccessService.getUserAccessTier(newState);
      if (nextTier !== lastTier || newState.sessionToken !== lastToken) {
        lastTier = nextTier;
        lastToken = newState.sessionToken;
        if (newState.manifest) {
          this.closeChartsAndMaps();
          const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
          const reqs = getRequirementsForPersona(newState.activePersona || 'default', isMobile);
          try {
            const refreshed = await PoiDataService.fetchFullForecast(
              this.currentLat,
              this.currentLon,
              this.currentElev,
              this.currentBaseElev,
              this.currentDateStr,
              newState.manifest,
              this.isMountain,
              this.activeTabId === 'climate' ? 'blend' : this.activeTabId,
              this.viewDays,
              reqs
            );
            this.currentForecastData = refreshed;
            this.renderModalContent(this.currentLat, this.currentLon, this.currentElev, refreshed);
          } catch (err) {
            console.warn('[PoiModal] Failed to refresh forecast after auth tier change:', err);
          }
        }
        return;
      }
      if (newState.activePersona && newState.activePersona !== lastPersona) {
        lastPersona = newState.activePersona;
        if (this.currentForecastData) {
          this.closeChartsAndMaps();
          this.renderModalContent(this.currentLat, this.currentLon, this.currentElev, this.currentForecastData);
        }
      }
    });
    
    try {
      const baseElev = await getBaseElevationAt(lat, lon);
      this.currentBaseElev = baseElev;
      
      const isUS = isPointInUS(lat, lon);
      const isCA = isPointInCanada(lat, lon);
      if (defaultTab && defaultTab !== 'auto') {
        this.selectedSource = defaultTab;
        this.activeTabId = defaultTab;
      } else {
        this.selectedSource = 'auto';
        this.activeTabId = isUS ? 'nws' : (isCA ? 'eccc' : 'ecmwf');
      }

      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
      const initialPersonaId = state.activePersona || 'default';
      const reqs = getRequirementsForPersona(initialPersonaId, isMobile);
      const data = await PoiDataService.fetchFullForecast(
        lat, lon, elev, baseElev, this.currentDateStr, state.manifest!, this.isMountain, 
        this.activeTabId === 'climate' ? 'blend' : this.activeTabId, this.viewDays, reqs
      );
      this.currentForecastData = data;
      
      this.renderModalContent(lat, lon, elev, data);
    } catch (e) {
      if (this.activeTabId === 'climate') {
        this.renderModalContent(lat, lon, elev, {} as any);
      } else {
        this.container.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #ef4444; font-size: 1.25rem;">Failed to load detailed forecast.</div>`;
        console.error(e);
      }
    }
  }

  private renderModalContent(lat: number, lon: number, elev: number, data: DetailedForecastData) {
    const activePersona = getActivePersona();
    const isImperial = store.getState().units === 'imperial';
    
    const layout = getLayoutForPersona(activePersona.id);
    const soundingRef = { chart: this.soundingChart };

    const hasHorizonWidget = (layout.widgets || []).some(w => w.type === 'horizon_selector' || w.type === 'horizon-selector');

    const handleToggle = async (daysVal: number | string) => {
      if (this.viewDays === daysVal) return;
      this.viewDays = daysVal;
      this.closeChartsAndMaps();
      this.container.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: white; font-size: 1.5rem;">Loading Forecast Data...</div>`;
      try {
        const state = store.getState();
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        const reqs = getRequirementsForPersona(activePersona.id, isMobile);
        const newData = await PoiDataService.fetchFullForecast(
          this.currentLat, this.currentLon, this.currentElev, this.currentBaseElev, 
          this.currentDateStr, state.manifest!, this.isMountain, this.activeTabId, daysVal, reqs
        );
        this.currentForecastData = newData;
        this.renderModalContent(this.currentLat, this.currentLon, this.currentElev, newData);
      } catch (e) {
        this.container.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #ef4444; font-size: 1.25rem;">Failed to load detailed forecast.</div>`;
        console.error(e);
      }
    };

    const handleSourceChange = async (source: string) => {
      this.selectedSource = source;
      let targetTab = source;
      if (source === 'auto') {
        const isUS = isPointInUS(this.currentLat, this.currentLon);
        const isCA = isPointInCanada(this.currentLat, this.currentLon);
        targetTab = isUS ? 'nws' : (isCA ? 'eccc' : 'ecmwf');
      }
      if (this.activeTabId === targetTab && source !== 'auto') return;
      this.activeTabId = targetTab;
      this.closeChartsAndMaps();
      this.container.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: white; font-size: 1.5rem;">Loading Forecast Data (${targetTab.toUpperCase()})...</div>`;
      try {
        const state = store.getState();
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        const reqs = getRequirementsForPersona(activePersona.id, isMobile);
        const newData = await PoiDataService.fetchFullForecast(
          this.currentLat, this.currentLon, this.currentElev, this.currentBaseElev, 
          this.currentDateStr, state.manifest!, this.isMountain, targetTab, this.viewDays, reqs
        );
        this.currentForecastData = newData;
        this.renderModalContent(this.currentLat, this.currentLon, this.currentElev, newData);
      } catch (e) {
        this.container.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #ef4444; font-size: 1.25rem;">Failed to load detailed forecast.</div>`;
        console.error(e);
      }
    };

    const renderCtx: WidgetRenderContext = {
      lat,
      lon,
      elev,
      dateStr: this.currentDateStr,
      viewDays: this.viewDays,
      isMountain: this.isMountain,
      data,
      manifest: store.getState().manifest,
      activePersonaId: activePersona.id,
      activeTabId: this.activeTabId,
      charts: this.charts,
      maps: this.lightningMaps,
      soundingChartRef: soundingRef,
      onHorizonChange: (val: number | string) => handleToggle(val),
      onSourceChange: (source: string) => handleSourceChange(source)
    };
    
    const isUS = isPointInUS(lat, lon);
    const isCA = isPointInCanada(lat, lon);
    const isHRRR = isModelAvailableAt('hrrr', lat, lon);
    const isRRFS = isModelAvailableAt('rrfs', lat, lon);
    const isRDPS = isModelAvailableAt('rdps', lat, lon);
    const isBlend = isModelAvailableAt('blend', lat, lon);

    const elevDisplay = isImperial ? `${Math.round(elev)} ft` : `${Math.round(elev / 3.28084)} m`;

    const reqMs = new Date(`${this.currentDateStr}T12:00:00Z`).getTime();
    const isFarFuture = reqMs > Date.now() + 10 * 86400 * 1000;

    const currentHorizonStr = String(this.viewDays).toLowerCase();

    const html = `
      <div style="width: 90vw; height: 90vh; background: #0f172a; border-radius: 12px; border: 1px solid var(--border-light); display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);">
        
        <!-- Header -->
        <div style="padding: 12px 16px; border-bottom: 1px solid var(--border-light); display: flex; justify-content: space-between; align-items: center; background: #1e293b;">
          <div>
            <h2 style="margin: 0; font-size: 1.2rem; color: #f8fafc; display: flex; align-items: center; gap: 10px;">
              <span>${this.activeTabId === 'climate' ? '30-Year Climatology & Climate Normals' : 'Full Detailed Forecast'}</span>
              ${this.activeTabId !== 'climate' && isFarFuture ? `
                <span style="font-size: 0.7rem; color: #f59e0b; background: rgba(245, 158, 11, 0.15); border: 1px solid rgba(245, 158, 11, 0.35); padding: 2px 8px; border-radius: 4px; font-weight: 600;">
                  Weather Capped at 10-Day Max Horizon
                </span>
              ` : ''}
            </h2>
            <div style="display: flex; align-items: center; gap: 16px; margin-top: 4px; flex-wrap: wrap;">
              <span style="color: #94a3b8; font-size: 0.8rem;">
                ${this.activeTabId === 'climate' 
                  ? `Location: Lat ${lat.toFixed(4)}°, Lon ${lon.toFixed(4)}° | Elevation: ${elevDisplay} | 1991–2020 Multi-Decade Baseline`
                  : `Forecast For: ${data.localDate} ${data.localTime} ${data.timezoneAbbr} | Lat: ${lat.toFixed(4)}, Lon: ${lon.toFixed(4)} | Elevation: ${elevDisplay}`}
              </span>
              ${this.activeTabId !== 'climate' ? `
              <div style="display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                <!-- Model / Data Source Dropdown -->
                <div class="poi-header-source-box" style="display: inline-flex; align-items: center; gap: 6px; background: rgba(0,0,0,0.35); padding: 2px 8px; border-radius: 6px; border: 1px solid var(--border-light);">
                  <span style="font-size: 0.7rem; color: #94a3b8; font-weight: 600;">MODEL:</span>
                  <select id="poi-header-source-select" style="background: rgba(56, 189, 248, 0.12); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.35); border-radius: 4px; padding: 2px 6px; font-size: 0.72rem; font-weight: 700; cursor: pointer; outline: none;">
                    <option value="auto" ${this.selectedSource === 'auto' ? 'selected' : ''}>Auto-detect (${isUS ? 'NWS' : (isCA ? 'ECCC' : 'ECMWF')})</option>
                    ${isUS ? `<option value="nws" ${this.selectedSource === 'nws' ? 'selected' : ''}>NWS (NOAA Official)</option>` : ''}
                    ${isCA ? `<option value="eccc" ${this.selectedSource === 'eccc' ? 'selected' : ''}>ECCC (Canada Official)</option>` : ''}
                    ${isBlend ? `<option value="blend" ${this.selectedSource === 'blend' || this.selectedSource === 'nbm' ? 'selected' : ''}>NBM (National Blend)</option>` : ''}
                    ${isHRRR ? `<option value="hrrr" ${this.selectedSource === 'hrrr' ? 'selected' : ''}>HRRR (3km High-Res)</option>` : ''}
                    ${isRRFS ? `<option value="rrfs" ${this.selectedSource === 'rrfs' ? 'selected' : ''}>RRFS (3km Rapid Refresh)</option>` : ''}
                    ${isRDPS ? `<option value="rdps" ${this.selectedSource === 'rdps' ? 'selected' : ''}>RDPS (10km Canada/N.Amer)</option>` : ''}
                    <option value="gdps" ${this.selectedSource === 'gdps' ? 'selected' : ''}>GDPS (15km Global Canada)</option>
                    <option value="ecmwf" ${this.selectedSource === 'ecmwf' ? 'selected' : ''}>ECMWF (0.1° IFS HRES)</option>
                    <option value="gfs" ${this.selectedSource === 'gfs' ? 'selected' : ''}>GFS (0.25° Global)</option>
                    <option value="icon13" ${this.selectedSource === 'icon13' ? 'selected' : ''}>ICON-13 (DWD Global)</option>
                  </select>
                </div>
                ${!hasHorizonWidget ? `
                  <div class="poi-header-horizon-box" style="display: inline-flex; background: rgba(0,0,0,0.35); padding: 2px; border-radius: 6px; border: 1px solid var(--border-light); gap: 2px;">
                    <button class="poi-view-horizon-btn" data-horizon="1" style="padding: 3px 10px; font-size: 0.72rem; border-radius: 4px; border: none; cursor: pointer; transition: all 0.2s; ${currentHorizonStr === '1' ? 'background: #3b82f6; color: #fff; font-weight: bold;' : 'background: transparent; color: #94a3b8;'}">1-Day</button>
                    <button class="poi-view-horizon-btn" data-horizon="3" style="padding: 3px 10px; font-size: 0.72rem; border-radius: 4px; border: none; cursor: pointer; transition: all 0.2s; ${currentHorizonStr === '3' ? 'background: #3b82f6; color: #fff; font-weight: bold;' : 'background: transparent; color: #94a3b8;'}">3-Day</button>
                    <button class="poi-view-horizon-btn" data-horizon="5" style="padding: 3px 10px; font-size: 0.72rem; border-radius: 4px; border: none; cursor: pointer; transition: all 0.2s; ${currentHorizonStr === '5' ? 'background: #3b82f6; color: #fff; font-weight: bold;' : 'background: transparent; color: #94a3b8;'}">5-Day</button>
                    <button class="poi-view-horizon-btn" data-horizon="max" style="padding: 3px 10px; font-size: 0.72rem; border-radius: 4px; border: none; cursor: pointer; transition: all 0.2s; ${currentHorizonStr === 'max' ? 'background: #3b82f6; color: #fff; font-weight: bold;' : 'background: transparent; color: #94a3b8;'}">Max</button>
                  </div>
                ` : ''}
                ${activePersona.forecastPanel.showMilkyWayArButton ? `
                  <button id="poi-ar-view-btn" style="padding: 3px 10px; font-size: 0.72rem; border-radius: 4px; border: 1px solid #6366f1; background: rgba(99, 102, 241, 0.3); color: #a5b4fc; font-weight: bold; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; transition: all 0.2s;">
                    Show Milky Way & Lunar Details
                  </button>
                ` : ''}
              </div>
              ` : ''}
            </div>
          </div>
          <button id="poi-modal-close" style="background: transparent; border: none; color: #94a3b8; font-size: 1.5rem; cursor: pointer;">&times;</button>
        </div>

        <!-- Scrollable Content -->
        <div id="poi-modal-scrollable-content" style="flex: 1; overflow-y: auto; padding: 16px;">
          ${this.activeTabId === 'climate' ? this.renderClimateTabHtml() : renderLayoutHtml(layout, renderCtx)}
        </div>
      </div>
    `;

    this.container.innerHTML = html;

    const closeBtn = document.getElementById('poi-modal-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    const sourceSelect = this.container.querySelector<HTMLSelectElement>('#poi-header-source-select');
    if (sourceSelect) {
      sourceSelect.addEventListener('change', () => {
        handleSourceChange(sourceSelect.value);
      });
    }

    // Connect top header horizon selector buttons (when not using in-layout widget)
    const horizonButtons = this.container.querySelectorAll<HTMLButtonElement>('.poi-view-horizon-btn');
    horizonButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const valStr = btn.getAttribute('data-horizon');
        if (!valStr) return;
        const val = valStr === 'max' ? 'max' : parseInt(valStr, 10);
        handleToggle(val);
      });
    });

    const arBtn = this.container.querySelector('#poi-ar-view-btn');
    if (arBtn) {
      arBtn.addEventListener('click', () => {
        const currentStep = store.getState().currentTimeStep || new Date();
        const midnightDate = new Date(currentStep);
        midnightDate.setHours(0, 0, 0, 0);

        store.update({
          currentTimeStep: midnightDate,
          milkyWayArEnabled: true
        });
        this.close();
      });
    }



    // Initialize Charts & Modular Layout Widgets
    if (this.activeTabId === 'climate') {
      this.initClimateCharts(lat, lon, elev);
    } else {
      initLayoutWidgets(layout, renderCtx);
      this.soundingChart = soundingRef.chart;
    }
  }

  private renderClimateTabHtml(): string {
    return `
      <div id="poi-climate-tab-container" style="display: flex; flex-direction: column; gap: 16px; width: 100%; color: #f8fafc;">
        
        <!-- Top Section: 3-Column Elevation Microclimate Comparison -->
        <div class="metric-card" style="background: rgba(255,255,255,0.03); border: 1px solid var(--border-light); border-radius: 8px; padding: 14px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 8px;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <h3 style="margin: 0; font-size: 0.95rem; color: #f8fafc;">Microclimate Elevation Variance Modeling</h3>
            </div>
            <div style="position: relative; display: inline-block;">
              <span 
                id="poi-climate-help-icon"
                title="Environmental Lapse Rate: Estimates use standard -3.56°F per 1,000 ft elevation gain and orographic precipitation enhancement (+5% per 1,000 ft) to account for terrain effects across mountain valleys and ridge summits."
                style="cursor: help; font-size: 0.85rem; background: rgba(56, 189, 248, 0.2); border: 1px solid rgba(56, 189, 248, 0.4); color: #38bdf8; border-radius: 50%; width: 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; font-weight: bold;">
                i
              </span>
            </div>
          </div>

          <div id="poi-climate-elevation-grid" style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; font-size: 0.80rem;">
            <div style="text-align: center; color: #94a3b8; padding: 20px; grid-column: span 3;">Loading 30-Year Climatology data...</div>
          </div>
        </div>

        <!-- Middle Charts Grid: 365-Day Temperature & 52-Week Snowpack -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
          
          <!-- 365-Day Chart -->
          <div class="metric-card" style="background: rgba(255,255,255,0.03); border: 1px solid var(--border-light); border-radius: 8px; padding: 12px;">
            <h3 style="margin: 0 0 10px 0; color: #f8fafc; font-size: 0.92rem;">365-Day Annual Climatological Cycle</h3>
            <div style="height: 220px; position: relative;">
              <canvas id="poi-climate-365-chart"></canvas>
            </div>
          </div>

          <!-- 52-Week Snowpack Chart -->
          <div class="metric-card" style="background: rgba(255,255,255,0.03); border: 1px solid var(--border-light); border-radius: 8px; padding: 12px;">
            <h3 style="margin: 0 0 10px 0; color: #f8fafc; font-size: 0.92rem;">52-Week Snowpack & SWE Progression</h3>
            <div style="height: 220px; position: relative;">
              <canvas id="poi-climate-snowpack-chart"></canvas>
            </div>
          </div>

        </div>

        <!-- Bottom Table: 12-Month Normals Breakdown -->
        <div class="metric-card" style="background: rgba(255,255,255,0.03); border: 1px solid var(--border-light); border-radius: 8px; padding: 12px; overflow-x: auto;">
          <h3 style="margin: 0 0 10px 0; color: #f8fafc; font-size: 0.92rem;">12-Month Climate Normals (1991–2020 30-Yr Baseline)</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 0.75rem; text-align: right;">
            <thead>
              <tr style="border-bottom: 1px solid rgba(255,255,255,0.15); color: #94a3b8;">
                <th style="text-align: left; padding: 4px 6px;">Month</th>
                <th style="padding: 4px 6px;">Precip (in)</th>
                <th style="padding: 4px 6px;">Snow (in)</th>
                <th style="padding: 4px 6px;">Precip Days</th>
                <th style="padding: 4px 6px;">Days >90°F</th>
                <th style="padding: 4px 6px;">Days >100°F</th>
                <th style="padding: 4px 6px;">Days <30°F</th>
                <th style="padding: 4px 6px;">Days <0°F</th>
                <th style="padding: 4px 6px;">Avg Wind (mph)</th>
              </tr>
            </thead>
            <tbody id="poi-climate-monthly-tbody">
              <tr><td colspan="9" style="text-align: center; color: #94a3b8; padding: 15px;">Loading 12-Month Climate Normals...</td></tr>
            </tbody>
          </table>
        </div>

      </div>
    `;
  }

  private async initClimateCharts(lat: number, lon: number, elev: number) {
    const ctx365 = document.getElementById('poi-climate-365-chart') as HTMLCanvasElement;
    const ctxSnow = document.getElementById('poi-climate-snowpack-chart') as HTMLCanvasElement;
    if (!ctx365 || !ctxSnow) return;

    const state = store.getState();
    const minElev = state.selectedPoi?.minElev !== undefined ? state.selectedPoi.minElev : Math.max(0, elev - 1200);
    const maxElev = state.selectedPoi?.maxElev !== undefined ? state.selectedPoi.maxElev : (elev + 1800);
    const isImperial = state.units === 'imperial';
    const fmtElev = (ft: number) => isImperial ? `${Math.round(ft).toLocaleString()} ft` : `${Math.round(ft / 3.28084).toLocaleString()} m`;

    const data = await ClimateService.fetchClimatePointData(state.manifest!, lat, lon, elev, minElev, maxElev);
    if (!data) {
      if (ClimateService.lastAccessDenial || !TierAccessService.userHasLayerAccess('climate_data')) {
        const denialHtml = TierAccessService.buildTrendChartDenialHtml({
          denial: ClimateService.lastAccessDenial,
          requiredTier: 'registered',
          featureLabel: '30-year climatology & snowpack trend charts'
        });
        const elevGrid = document.getElementById('poi-climate-elevation-grid');
        if (elevGrid) elevGrid.innerHTML = `<div style="grid-column: span 3;">${denialHtml}</div>`;
        if (ctx365.parentElement) {
          ctx365.style.display = 'none';
          ctx365.parentElement.innerHTML = denialHtml;
        }
        if (ctxSnow.parentElement) {
          ctxSnow.style.display = 'none';
          ctxSnow.parentElement.innerHTML = denialHtml;
        }
        const tbody = document.getElementById('poi-climate-monthly-tbody');
        if (tbody) tbody.innerHTML = `<tr><td colspan="9" style="text-align: center; color: #94a3b8; padding: 15px;">Sign in with a free SkySkout account to view 12-month climate normals.</td></tr>`;
        return;
      }
      const elevGrid = document.getElementById('poi-climate-elevation-grid');
      if (elevGrid) elevGrid.innerHTML = `<div style="text-align: center; color: #94a3b8; padding: 20px; grid-column: span 3;">No Climatology data available for this location.</div>`;
      const tbody = document.getElementById('poi-climate-monthly-tbody');
      if (tbody) tbody.innerHTML = `<tr><td colspan="9" style="text-align: center; color: #94a3b8; padding: 15px;">No Data</td></tr>`;
      return;
    }

    // Render Microclimate Elevation Cards
    const prof = data.elevationProfile;
    const elevGrid = document.getElementById('poi-climate-elevation-grid');
    if (elevGrid) {
      elevGrid.innerHTML = `
        <!-- Column 1: Valley Floor (Min) -->
        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 10px; display: flex; flex-direction: column; gap: 6px;">
          <div style="font-weight: 800; color: #38bdf8; font-size: 0.85rem; border-bottom: 1px solid rgba(56,189,248,0.2); padding-bottom: 4px;">Valley Floor (Min)</div>
          <div style="color: #94a3b8; font-size: 0.72rem;">Altitude: <span style="color: #fff; font-weight: bold;">${fmtElev(prof.minElevFt)}</span></div>
          <div>Avg High: <span style="color: #fb923c; font-weight: bold;">${prof.valleyAdj.tmaxF}°F</span></div>
          <div>Avg Low: <span style="color: #38bdf8; font-weight: bold;">${prof.valleyAdj.tminF}°F</span></div>
          <div>Annual Precip: <span style="color: #60a5fa; font-weight: bold;">${prof.valleyAdj.precipInches}"</span> <span style="font-size: 0.68rem; color: #94a3b8;">(${Math.round(prof.valleyAdj.precipMult * 100)}%)</span></div>
          <div>Annual Snow: <span style="color: #c084fc; font-weight: bold;">${prof.valleyAdj.snowInches}"</span></div>
          <div>Peak Snowpack: <span style="color: #e2e8f0; font-weight: bold;">${prof.valleyAdj.snowpackInches}"</span></div>
        </div>

        <!-- Column 2: Grid Baseline (Mean) -->
        <div style="background: rgba(30, 41, 59, 0.7); border: 1px solid rgba(56,189,248,0.3); border-radius: 6px; padding: 10px; display: flex; flex-direction: column; gap: 6px; box-shadow: 0 0 12px rgba(56,189,248,0.15);">
          <div style="font-weight: 800; color: #f8fafc; font-size: 0.85rem; border-bottom: 1px solid rgba(255,255,255,0.2); padding-bottom: 4px; display: flex; justify-content: space-between;">
            <span>Grid Baseline</span>
            <span style="font-size: 0.65rem; background: #3b82f6; color: #fff; padding: 1px 5px; border-radius: 3px;">BASE</span>
          </div>
          <div style="color: #94a3b8; font-size: 0.72rem;">Altitude: <span style="color: #fff; font-weight: bold;">${fmtElev(prof.baseElevFt)}</span></div>
          <div>Avg High: <span style="color: #fb923c; font-weight: bold;">${prof.baseAdj.tmaxF}°F</span></div>
          <div>Avg Low: <span style="color: #38bdf8; font-weight: bold;">${prof.baseAdj.tminF}°F</span></div>
          <div>Annual Precip: <span style="color: #60a5fa; font-weight: bold;">${prof.baseAdj.precipInches}"</span> <span style="font-size: 0.68rem; color: #94a3b8;">(100%)</span></div>
          <div>Annual Snow: <span style="color: #c084fc; font-weight: bold;">${prof.baseAdj.snowInches}"</span></div>
          <div>Peak Snowpack: <span style="color: #e2e8f0; font-weight: bold;">${prof.baseAdj.snowpackInches}"</span></div>
        </div>

        <!-- Column 3: Peak / Ridge (Max) -->
        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 10px; display: flex; flex-direction: column; gap: 6px;">
          <div style="font-weight: 800; color: #f472b6; font-size: 0.85rem; border-bottom: 1px solid rgba(244,114,182,0.2); padding-bottom: 4px;">Summit / Ridge (Max)</div>
          <div style="color: #94a3b8; font-size: 0.72rem;">Altitude: <span style="color: #fff; font-weight: bold;">${fmtElev(prof.maxElevFt)}</span></div>
          <div>Avg High: <span style="color: #fb923c; font-weight: bold;">${prof.peakAdj.tmaxF}°F</span></div>
          <div>Avg Low: <span style="color: #38bdf8; font-weight: bold;">${prof.peakAdj.tminF}°F</span></div>
          <div>Annual Precip: <span style="color: #60a5fa; font-weight: bold;">${prof.peakAdj.precipInches}"</span> <span style="font-size: 0.68rem; color: #94a3b8;">(${Math.round(prof.peakAdj.precipMult * 100)}%)</span></div>
          <div>Annual Snow: <span style="color: #c084fc; font-weight: bold;">${prof.peakAdj.snowInches}"</span></div>
          <div>Peak Snowpack: <span style="color: #e2e8f0; font-weight: bold;">${prof.peakAdj.snowpackInches}"</span></div>
        </div>
      `;
    }

    // Render 12-Month Normals Table
    const tbody = document.getElementById('poi-climate-monthly-tbody');
    if (tbody) {
      tbody.innerHTML = data.months.map(m => `
        <tr style="border-bottom: 1px solid rgba(255,255,255,0.04);">
          <td style="text-align: left; padding: 4px 6px; font-weight: 700; color: #f8fafc;">${m.monthName}</td>
          <td style="padding: 4px 6px; color: #60a5fa;">${m.precipInches}"</td>
          <td style="padding: 4px 6px; color: #c084fc;">${m.snowInches > 0 ? `${m.snowInches}"` : '0"'}</td>
          <td style="padding: 4px 6px;">${m.precipDays}</td>
          <td style="padding: 4px 6px; color: ${m.daysGt90 > 0 ? '#fb923c' : '#94a3b8'};">${m.daysGt90}</td>
          <td style="padding: 4px 6px; color: ${m.daysGt100 > 0 ? '#ef4444' : '#94a3b8'};">${m.daysGt100}</td>
          <td style="padding: 4px 6px; color: ${m.daysLt30 > 0 ? '#38bdf8' : '#94a3b8'};">${m.daysLt30}</td>
          <td style="padding: 4px 6px; color: ${m.daysLt0 > 0 ? '#818cf8' : '#94a3b8'};">${m.daysLt0}</td>
          <td style="padding: 4px 6px; color: #34d399;">${m.windSpeedMph}</td>
        </tr>
      `).join('');
    }

    // 1. 365-Day Chart
    const monthMarkers = ['Jan 1', 'Feb 1', 'Mar 1', 'Apr 1', 'May 1', 'Jun 1', 'Jul 1', 'Aug 1', 'Sep 1', 'Oct 1', 'Nov 1', 'Dec 1'];
    const labels365 = data.days.map((d) => {
      if (d.dayNum === 1) return monthMarkers[d.monthNum - 1];
      return '';
    });

    const chart365 = new Chart(ctx365, {
      type: 'line',
      data: {
        labels: labels365,
        datasets: [
          {
            label: 'Record High',
            data: data.days.map(d => d.tmaxRec),
            borderColor: '#dc2626',
            borderDash: [4, 4],
            borderWidth: 1,
            pointRadius: 0,
            yAxisID: 'y'
          },
          {
            label: '90% High',
            data: data.days.map(d => d.tmaxP90),
            borderColor: '#f97316',
            borderWidth: 1.5,
            pointRadius: 0,
            yAxisID: 'y'
          },
          {
            label: 'Average High',
            data: data.days.map(d => d.tmaxMean),
            borderColor: '#eab308',
            borderWidth: 2.5,
            pointRadius: 0,
            yAxisID: 'y'
          },
          {
            label: 'Average Low',
            data: data.days.map(d => d.tminMean),
            borderColor: '#06b6d4',
            borderWidth: 2.5,
            pointRadius: 0,
            yAxisID: 'y'
          },
          {
            label: '90% Low',
            data: data.days.map(d => d.tminP90),
            borderColor: '#3b82f6',
            borderWidth: 1.5,
            pointRadius: 0,
            yAxisID: 'y'
          },
          {
            label: 'Record Low',
            data: data.days.map(d => d.tminRec),
            borderColor: '#4f46e5',
            borderDash: [4, 4],
            borderWidth: 1,
            pointRadius: 0,
            yAxisID: 'y'
          },
          {
            label: 'Precip Chance (%)',
            data: data.days.map(d => d.precipProb),
            type: 'bar' as any,
            backgroundColor: 'rgba(56, 189, 248, 0.3)',
            yAxisID: 'y1',
            order: 99
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { ticks: { color: '#94a3b8', font: { size: 9 }, autoSkip: false }, grid: { display: false } },
          y: {
            ticks: { color: '#94a3b8', font: { size: 9 } },
            grid: { color: 'rgba(255,255,255,0.05)' },
            title: { display: true, text: 'Temp (°F)', color: '#fb923c', font: { size: 9 } }
          },
          y1: {
            position: 'right',
            min: 0,
            max: 100,
            ticks: { color: '#38bdf8', font: { size: 9 } },
            grid: { display: false },
            title: { display: true, text: 'Precip (%)', color: '#38bdf8', font: { size: 9 } }
          }
        },
        plugins: {
          legend: { labels: { color: '#f8fafc', boxWidth: 10, font: { size: 9 } } }
        }
      }
    });
    this.charts.push(chart365);

    // 2. 52-Week Snowpack Chart
    const chartSnow = new Chart(ctxSnow, {
      type: 'line',
      data: {
        labels: data.weeks.map(w => w.weekLabel),
        datasets: [
          {
            label: 'Snowpack Depth (in)',
            data: data.weeks.map(w => w.snowDepthIn),
            borderColor: '#38bdf8',
            backgroundColor: 'rgba(56, 189, 248, 0.25)',
            fill: true,
            tension: 0.3,
            borderWidth: 2,
            pointRadius: 0
          },
          {
            label: 'Snow Water Equivalent (in)',
            data: data.weeks.map(w => w.snowSweIn),
            borderColor: '#818cf8',
            borderWidth: 1.5,
            borderDash: [3, 3],
            pointRadius: 0
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { ticks: { color: '#94a3b8', font: { size: 8 }, maxTicksLimit: 12 }, grid: { display: false } },
          y: {
            ticks: { color: '#94a3b8', font: { size: 9 } },
            grid: { color: 'rgba(255,255,255,0.05)' },
            title: { display: true, text: 'Inches', color: '#38bdf8', font: { size: 9 } }
          }
        },
        plugins: {
          legend: { labels: { color: '#f8fafc', boxWidth: 10, font: { size: 9 } } }
        }
      }
    });
    this.charts.push(chartSnow);
  }
}

export const poiModal = new PoiModal();
